(ns manage.adset-preview-panel.view.sidebar
  (:require ["@mui/material/Button$default" :as Button]
            ["@mui/material/Divider$default" :as Divider]
            ["@mui/material/Drawer$default" :as Drawer]
            ["@mui/material/Icon$default" :as Icon]
            ["@mui/material/IconButton$default" :as IconButton]
            ["@mui/material/Stack$default" :as Stack]
            ["@mui/material/Typography$default" :as Typography]
            [clojure.string :as string]
            [common.queries :refer [use-query]]
            [common.resource.query.poi :as poi-query]
            [common.widgets.dropdown :refer [dropdown]]
            [common.widgets.editable-label :as label]
            [common.widgets.form-field :refer [form-field]]
            [manage.adset-preview-panel.view.context-field :refer [context-field]]
            [re-frame.core :as rf]
            [reagent.core :as r]
            [uix.core :as uix :refer [$ defui]]
            [uix.re-frame :refer [use-subscribe]]))

(defui settings
  "Hiccup rendering settings like auto-replay"
  []
  ($ :div
     ($ Button {:onClick #(rf/dispatch [:adset-preview.variants/get-your-context])
                :variant "text"
                :startIcon ($ Icon "my_location")
                :data-testid "use-your-context"} "Use your context")
     ($ Button {:onClick #(rf/dispatch [:adset-preview.variants/clear-variant-context])
                :variant "text"
                :startIcon ($ Icon "close")
                :data-testid "clear-all-context"} "Clear all context")))

(defui poi-location-picker []
  (let [{:keys [advertiser-id]} (use-subscribe [:url-context])
        schema-urns (use-subscribe [:adset-preview.core/value :schema-urns])
        has-poi? (boolean (some #(= "poi" (:alias %)) schema-urns))
        {:keys [data isFetching]} (use-query (poi-query/locations-query
                                              {:advertiser-id advertiser-id
                                               :per-page 1000
                                               :enabled? has-poi?}))
        locations (:items data)
        id->location (uix/use-memo
                      (fn [] (into {} (map (juxt :id identity)) locations))
                      [locations])
        options (uix/use-memo
                 (fn []
                   (->> locations
                        (mapv (fn [loc]
                                {:label (str (get-in loc [:data :name]) " (" (:external-id loc) ")")
                                 :value (:id loc)}))
                        (sort-by (comp string/lower-case :label))))
                 [locations])
        [selected set-selected!] (uix/use-state nil)
        use-request-context? (use-subscribe [:adset-preview.variants/use-request-context?])
        variant-fields (use-subscribe [:adset-preview.variants/selected-variant-fields])
        poi-has-value? (some #(and (= :poi (:type %)) (some? (:value %))) variant-fields)]
    (uix/use-effect
     (fn []
       (when (or use-request-context? (not poi-has-value?))
         (set-selected! nil)))
     [use-request-context? poi-has-value?])
    (when has-poi?
      ($ form-field
         {:label "POI Location"
          :data-testid "poi-location-picker"}
         (r/as-element
          [dropdown
           {:options options
            :value selected
            :on-change (fn [id]
                         (set-selected! id)
                         (when-let [loc (get id->location id)]
                           (rf/dispatch [:adset-preview.variants/select-poi-location loc])))
            :placeholder (if isFetching "Loading locations..." "Select a location")
            :clearable? false}])))))

(defui variant-context-list
  "Hiccup for rendering the selected variant context list"
  []
  (let [variant-fields (use-subscribe [:adset-preview.variants/selected-variant-fields])
        on-value-change (fn [context-name value]
                          (rf/dispatch [:adset-preview.variants/context-value-change
                                        {:context-name context-name}
                                        value]))]
    ($ Stack {:spacing 2}
       ($ poi-location-picker)
       (for [{:keys [name value field type]} variant-fields
             :when (not= :poi type)]
         ($ form-field
            {:key name
             :label name
             :data-testid name}
            (r/as-element
             [context-field (merge {:value value
                                    :on-change #(on-value-change name %)}
                                   field)]))))))

(defui product-list
  "Hiccup for rendering the product list for the product-selector"
  []
  (let [products (use-subscribe [:adset-preview.products/products-for-current-selector])]
    ($ Stack {:spacing 2}
       (for [idx (range 10)]
         (let [product (when (< idx (count products)) (get products idx))
               set-product #(rf/dispatch [:adset-preview.products/set-product idx %])]
           ($ form-field
              {:key (str "products-" (:sku product) "-" idx)
               :label (str "Product " (inc idx))}
              ($ label/clickable-label
                 {:value (:sku product)
                  :on-clear #(rf/dispatch [:adset-preview.products/delete-product idx])
                  :on-click #(rf/dispatch [:modal/open
                                           {:id :product-modal
                                            :props {:on-select set-product
                                                    :disabled-products products}}])})))))))

(defn preview-sidebar [{:keys [mobile? open title on-close header-children]} body]
  [:> Drawer {:open open
              :variant "persistent"
              :anchor "left"
              :data-testid :sidebar
              :sx (cond-> {"& .MuiDrawer-paper" {:position "absolute"
                                                 :inset 0}}
                    (not mobile?) (assoc :position "relative")
                    open (assoc :width "35rem"))}
   [:> Stack {:spacing 2 :p 3}
    [:> Stack {:direction "row" :align-items "center" :justify-content "space-between"}
     [:> Typography {:variant "h3"
                     :data-testid :sidebar-title} title]
     [:> IconButton {:on-click on-close
                     :data-testid :close-sidebar-button}
      [:> Icon {:data-testid :sidebar-close-button} "close"]]]
    (when header-children
      header-children)]
   [:> Divider]
   [:> Stack {:p 3 :sx {:overflow-y "auto"}}
    body]])

(defn sidebar-content [sidebar-visible]
  (case sidebar-visible
    :context {:title "Context"
              :header-children ($ settings)
              :body ($ variant-context-list)}
    :products {:title "Products"
               :header-children [dropdown
                                 {:multi? false
                                  :value-fn identity
                                  :value @(rf/subscribe [:adset-preview.products/current-product-selector])
                                  :options @(rf/subscribe [:adset-preview.products/value :product-selectors])
                                  :on-change #(rf/dispatch [:adset-preview.products/set-product-selector %])
                                  :clearable? false}]
               :body ($ product-list)}
    nil))

(defn sidebar
  "Hiccup of rendering the whole sidebar"
  [mobile?]
  (let [sidebar-visible @(rf/subscribe [:adset-preview.core/value :sidebar-visible])
        sidebar-content (sidebar-content sidebar-visible)]
    [preview-sidebar {:mobile? mobile?
                      :open (some? sidebar-visible)
                      :on-close #(rf/dispatch [:adset-preview.core/toggle-sidebar nil])
                      :title (:title sidebar-content)
                      :header-children (:header-children sidebar-content)}
     (:body sidebar-content)]))
